import React, { MouseEvent, Ref, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';

import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { FieldContained, errorCss } from '@ad-scripts/presentation/components/common/textarea_field';
import Input from 'src/presentation/components/forms/Input';
import { ModalContainer, StyledAddModelContainer, StyledEmbedModalWithGreenHeader, StyledFooter } from './Layout';
import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import { Row } from 'src/presentation/components/forms/Layout';
import SidekickModuleSpec from 'src/models/Sidekick/SidekickModuleSpec';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import { SidekickThreadFast } from 'src/models/Sidekick/SidekickThreads';
import { ErrorLabel } from '@ad-scripts/presentation/components/common/forms/control_container';
import sidekickFastThreadsResource from 'src/actions/sidekick/sidekickFastThreadsResource';

type NewThreadModalProps = {};

type ShowModalProps = {
  confirmButtonLabel: string;
  subscribe: (params: unknown) => void;
  thread: SidekickThreadFast | null;
  title: string;
  value?: string;
  label?: string;
  useSimpleInput?: boolean;
  placeholder?: string;
  requireValidation?: boolean;
};

type OnSelectCallback = (value: string) => void;

export type NewThreadModalRef = {
  show: ({
    title,
    confirmButtonLabel,
    subscribe,
    value,
    label,
    useSimpleInput,
    placeholder,
    requireValidation,
  }: ShowModalProps) => void;
};

const NewThreadModal = forwardRef((_: NewThreadModalProps, ref: Ref<NewThreadModalRef>) => {
  const refCallbacks = useRef<{ subscribe: (params: unknown) => void } | null>(null);
  const [moduleSpec, setModuleSpec] = useState<SidekickModuleSpec | null>(null);
  const [title, setTitle] = useState('');
  const [value, setValue] = useState('');
  const [thread, setThread] = useState<SidekickThreadFast | null>(null);
  const [confirmButtonLabel, setConfirmButtonLabel] = useState('Confirm');
  const [label, setLabel] = useState('');
  const [useSimpleInput, setUseSimpleInput] = useState(false);
  const [placeholder, setPlaceholder] = useState('');
  const [requireValidation, setRequireValidation] = useState(true);

  const [modalVisible, setModalVisible] = useState(false);

  useImperativeHandle(ref, () => ({
    show({
      title,
      confirmButtonLabel,
      subscribe,
      value,
      thread,
      label,
      useSimpleInput,
      placeholder,
      requireValidation,
    }: ShowModalProps) {
      setModuleSpec(moduleSpec || null);
      refCallbacks.current = { subscribe };
      setModalVisible(true);
      setTitle(title);
      setValue(value || '');
      setConfirmButtonLabel(confirmButtonLabel);
      setThread(thread);
      setLabel(label || '');
      setUseSimpleInput(useSimpleInput || false);
      setPlaceholder(placeholder || '');
      setRequireValidation(requireValidation ?? true);
    },
  }));

  const handleSelect = (value: string) => {
    if (refCallbacks.current?.subscribe) {
      refCallbacks.current.subscribe(value);
      handleClose();
    }
  };

  const handleClose = () => {
    setModalVisible(false);
    refCallbacks.current = null;
  };

  if (!modalVisible) {
    return null;
  }

  return (
    <StyledEmbedModalWithGreenHeader
      className="new-thread-modal"
      title={title}
      content={
        <NewThreadModalContent
          onSelect={handleSelect}
          onCancel={handleClose}
          initValue={value}
          confirmButtonLabel={confirmButtonLabel}
          threadId={thread?.id}
          label={label}
          useSimpleInput={useSimpleInput}
          placeholder={placeholder}
          requireValidation={requireValidation}
        />
      }
      disableOutsideClick={true}
      modalSize={ModalSize.DEFAULT}
      onCancel={handleClose}
    />
  );
});

const NewThreadModalContent = ({
  confirmButtonLabel,
  initValue,
  onCancel,
  onSelect,
  threadId,
  label,
  useSimpleInput,
  placeholder,
  requireValidation,
}: {
  confirmButtonLabel: string;
  initValue: string;
  onCancel: () => void;
  onSelect: OnSelectCallback;
  threadId?: string | null;
  label?: string;
  useSimpleInput: boolean;
  placeholder?: string;
  requireValidation: boolean;
}) => {
  const [error, setError] = useState<string>('');
  const [value, setValue] = useState<string>(initValue);
  const [initVal] = useState(initValue);

  const { values: threads } = useResourceValues(sidekickFastThreadsResource);

  const handleClick = (evn: MouseEvent<HTMLElement>) => {
    evn.preventDefault();
    onSelect(value.trim());
  };

  const handleChange = evt => {
    setError('');
    evt.preventDefault();
    const value = evt.target.value;
    validateDuplicated(value);

    setValue(value);
  };

  const validateDuplicated = (name: string) => {
    if (!requireValidation) {
      return;
    }

    if (!threads || !threads.length) {
      return;
    }

    const thread = threads.find(thread => thread.name === name.trim() && thread.id !== threadId);
    if (thread && thread.id !== threadId) {
      setError('Name already exists. Choose a new name.');
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && value && value !== initVal) {
      onSelect(value.trim());
    }
    if (event.key === 'Escape') {
      onCancel();
    }
  };

  const renderInput = () =>
    useSimpleInput ? (
      <SimpleInputContainer>
        <StyledLabel>{label}</StyledLabel>
        <StyledInput autoFocus value={value} onChange={handleChange} placeholder={placeholder || 'Chat Menu'} />
      </SimpleInputContainer>
    ) : (
      <StyledFieldContained
        error={error}
        label={label || 'Name'}
        control={
          <StyledInput
            attr=""
            data-aid="name"
            isError={!!error}
            value={value}
            onChange={handleChange}
            placeholder={placeholder || 'Chat Menu'}
            autoFocus
          />
        }
      />
    );

  const isDisabled = !!error || !value || (requireValidation && initVal === value);

  return (
    <StyledAddModelContainer onKeyDown={handleKeyDown}>
      <ModalContainer>
        <Row>{renderInput()}</Row>
      </ModalContainer>
      <StyledFooter>
        <Button onClick={onCancel} type={ButtonTypes.TEXT}>
          Cancel
        </Button>
        <Button data-aid="modal-create-button" onClick={handleClick} disabled={isDisabled}>
          {confirmButtonLabel}
        </Button>
      </StyledFooter>
    </StyledAddModelContainer>
  );
};

const StyledFieldContained = styled(FieldContained)`
  ${ErrorLabel} {
    padding-top: 8px;
    text-align: left;
  }
`;

const StyledInput = styled(Input)`
  ${p => p.isError && errorCss};
`;

export const StyledLabel = styled.label`
  align-self: flex-start;
  color: ${p => p.theme.colors.gray700};
  font-family: Inter;
  font-size: 13px;
  font-weight: 400;
  line-height: 21.06px;
  min-height: 18px;
  margin-bottom: 8px;
`;

export const SimpleInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export default NewThreadModal;
